import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAdvancedSearchKayttajatMutation } from "../api/backendApi";
import Iframe from "react-iframe";
import {
  Navigate,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import styled from "styled-components";

import {
  Heading,
  TextInput,
  Button,
  Table as SuomifiTable,
} from "suomifi-ui-components";
import { IconSearch, IconClose } from "suomifi-icons";

import { useSearchKayttajatQuery } from "../api/backendApi";

import { voiNahda, vainTasmaHaku } from "../utils/oikeudet";
import TarkennettuHaku from "../components/TarkennettuHaku";
import {
  naytaNimi,
  onTunnistautumaton,
  vahvistusStatus,
} from "../utils/kayttajaNimi";
import HenkilollisyysBadge from "../components/HenkilollisyysBadge";
import UidCopy from "../components/UidCopy";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { useTranslation } from "react-i18next";
import { Kayttaja } from "../types";
import countries, { alpha3ToAlpha2 } from "i18n-iso-countries";

/**
 * Käyttäjähaku — OR-haku UID/sähköposti/nimi -kentissä.
 *
 * Workshop 1.6.2026 B7-B9 / F1-F2: haku napin/Enter:in takana (ei live),
 * trim + whitespace-collapse, vähimmäismerkkimäärä 2 säilyy. Nykyportaalin
 * kipupiste (haastattelu 28.4.): kopioidut välilyönnit kaatavat haun →
 * normalisointi.
 *
 * - Hybridiriviklikkaus (Gmail/Linear-tyyli) avaa käyttäjäkortin. UidCopy
 *   pysäyttää klikin omalla `stopPropagation`-logiikallaan.
 * - Poistetut käyttäjät suodatetaan pois (F3, käyttäjän vahvistus).
 */

const MIN_QUERY_LENGTH = 2;

// ✅ Sort types
type SortKey = "kayttaja" | "uid" | "henkilollisyys" | "tila";
type SortDir = "asc" | "desc";

const VALIDIT_SORT_KEYT: SortKey[] = [
  "kayttaja",
  "uid",
  "henkilollisyys",
  "tila",
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Counter = styled.span`
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const SearchWrap = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
`;

const PerusHaku = styled.div`
  max-width: 50%;
  min-width: 24rem;
`;

const HakuToiminnot = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  display: flex;
  gap: ${({ theme }) => theme.spacing.s};
`;

const StatusMsg = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.m};
  text-align: center;
  color: ${({ theme }) => theme.colors.depthDark1};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
`;

const TableWrapper = styled.div`
  & > .fi-table {
    overflow: visible;
  }
  & > div {
    border: none !important;
  }

  table caption {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  && table {
    width: 100%;
    border: 0 !important;
    border-spacing: 0 !important;
    margin: 0 !important;
  }

  && table thead,
  && table thead tr,
  && table thead th {
    background: ${({ theme }) => theme.colors.depthLight3} !important;
    color: ${({ theme }) => theme.colors.brandBase} !important;
  }

  && table thead {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight1} !important;
    border-left: 1px solid ${({ theme }) => theme.colors.depthLight1} !important;
    border-right: 1px solid ${({ theme }) => theme.colors.depthLight1} !important;
    border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase} !important;
  }

  && table thead tr,
  && table thead th {
    border-top: 0 !important;
  }

  && table thead th,
  && table thead th button,
  && table thead th button *,
  && table thead th span {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
      "Helvetica Neue", "Noto Sans", Arial, sans-serif, "Apple Color Emoji",
      "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    font-style: normal;
    line-height: 18px !important;
    letter-spacing: 0.96px !important;
    text-transform: uppercase;
    color: rgb(0, 53, 122) !important;
  }

  && table thead th {
    text-align: left;
    padding: ${({ theme }) => theme.spacing.s}
      ${({ theme }) => theme.spacing.xxs};
  }

  && table thead th button,
  && table thead th button * {
    background: transparent !important;
  }

  && table thead th button {
    border: 0 !important;
    box-shadow: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
  }

  && table thead th button:hover {
    color: ${({ theme }) => theme.colors.highlightDark1} !important;
  }

  && table thead th button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }

  && table thead th button svg {
    width: 0.875rem !important;
    height: 0.875rem !important;
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.brandBase} !important;
  }

  && table thead th button svg path {
    stroke: currentColor;
  }

  && table thead th:first-child {
    padding-left: ${({ theme }) => theme.spacing.s};
  }

  && table thead th:last-child {
    padding-right: ${({ theme }) => theme.spacing.s};
  }

  table tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.highlightLight4};
    }

    &:focus-within {
      outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
      outline-offset: -2px;
    }
  }

  table tbody tr td {
    position: relative;
    padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
    vertical-align: top;
  }

  table tbody tr td button {
    position: relative;
    z-index: 1;
  }

  table tbody tr td a[data-user-link] {
    display: block;
    margin: ${({ theme }) => `-${theme.spacing.s} -${theme.spacing.m}`};
    padding: ${({ theme }) => `${theme.spacing.s} ${theme.spacing.m}`};
    color: inherit;
    text-decoration: none;
    cursor: pointer;
  }

  table tbody tr td a[data-user-link]::after {
    content: "";
    position: absolute;
    inset: 0;
  }

  /* UID copy controls must not trigger navigation. */
  table tbody tr td:nth-child(2) {
    cursor: default;
  }
`;

interface TableColumn {
  key: SortKey;
  headerName: string;
  width?: string;
  sortIcon?: "alphabetical" | "generic";
}

const columns: TableColumn[] = [
  { key: "kayttaja", headerName: "otsikot.kayttaja" },
  { key: "uid", headerName: "otsikot.uid", width: "14rem" },
  {
    key: "henkilollisyys",
    headerName: "otsikot.henkilollisyys",
    width: "13rem",
    sortIcon: "generic",
  },
  {
    key: "tila",
    headerName: "otsikot.tila",
    width: "12rem",
    sortIcon: "generic",
  },
];

const KayttajaPrimary = styled.div`
  color: ${({ theme }) => theme.colors.blackBase};
  font-size: 0.875rem;
`;

const KayttajaSecondary = styled.div`
  color: ${({ theme }) => theme.colors.depthDark1};
  font-size: 0.75rem;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const UidWrap = styled.div`
  color: ${({ theme }) => theme.colors.blackBase};
  font-size: 0.8125rem;
`;

const EiUid = styled.div`
  color: ${({ theme }) => theme.colors.depthDark1};
  font-size: 0.8125rem;
  font-style: italic;
`;

const VahvistusAlateksti = styled.div<{ $todennettu: boolean }>`
  color: ${({ theme, $todennettu }) =>
    $todennettu ? theme.colors.successDark1 : theme.colors.depthDark1};
  font-size: 0.6875rem;
  margin-top: ${({ theme }) => theme.spacing.insetXxs};
`;

const TilaTagit = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

const TilaTag = styled.span<{ $variant: "alert" | "warning" | "muted" }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.6875rem;
  background: ${({ theme, $variant }) => {
    if ($variant === "alert") return theme.colors.alertLight1;
    if ($variant === "warning") return theme.colors.warningLight1;
    return theme.colors.depthLight3;
  }};
  color: ${({ theme, $variant }) => {
    if ($variant === "alert") return theme.colors.alertBase;
    if ($variant === "warning") return theme.colors.blackBase;
    return theme.colors.depthDark1;
  }};
`;

function normalisoi(s: string): string {
  return s.trim().replace(/\s+/g, " ").toLowerCase();
}

export default function KayttajaHaku() {
  const { t, i18n } = useTranslation(["kayttajaLista", "common"]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { todellinenRooli, isLoading } = useTodellinenRooli();

  const submittedHaku = searchParams.get("haku") ?? "";
  const [hakuteksti, setHakuteksti] = useState(submittedHaku);
  const [advancedSearch] = useAdvancedSearchKayttajatMutation();
  const [lisakentat, setLisakentat] = useState<Record<string, string>>({});

  function setLisakentta(key: string, arvo: string) {
    setLisakentat((prev) => ({ ...prev, [key]: arvo }));
  }

  const taulukkoRef = useRef<HTMLTableElement>(null);
  const palautusTila = location.state as {
    palauttavaRivi?: string;
    listScrollY?: number;
  } | null;
  const palauttavaRivi = palautusTila?.palauttavaRivi ?? null;
  const palautusScrollY = palautusTila?.listScrollY;

  useLayoutEffect(() => {
    if (!palauttavaRivi) return;
    if (typeof palautusScrollY === "number") {
      window.scrollTo(0, palautusScrollY);
    }
    const row = taulukkoRef.current?.querySelector(
      `[data-kayttaja-uid="${palauttavaRivi}"]`,
    );
    if (row instanceof HTMLElement) {
      const r = row.getBoundingClientRect();
      const naekyvyydessa = r.top >= 0 && r.bottom <= window.innerHeight;
      if (!naekyvyydessa) {
        row.scrollIntoView({ block: "nearest", behavior: "auto" });
      }
    }
  }, [palauttavaRivi, palautusScrollY]);

  const tasmaHaku = vainTasmaHaku(todellinenRooli?.profiili);

  const haku = normalisoi(submittedHaku);
  const liianLyhyt = haku.length > 0 && haku.length < MIN_QUERY_LENGTH;

  function setHakuParam(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value.trim() === "") next.delete("haku");
    else next.set("haku", value);
    setSearchParams(next, { replace: true });
  }

  function handleHakuChange(value: string | number) {
    const v = String(value ?? "");
    setHakuteksti(v);
  }

  const { data: kayttajat = [], isLoading: kayttajatLataa } =
    useSearchKayttajatQuery(haku.length >= MIN_QUERY_LENGTH ? haku : null);

  const [advancedSearchResults, setAdvancedSearchResults] = useState<
    Kayttaja[]
  >([]);
  const [advancedSearchActive, setAdvancedSearchActive] = useState(false);

  // ✅ Add sort state from URL
  const sortKeyParam = searchParams.get("sort");
  const sortKey: SortKey | null =
    sortKeyParam && VALIDIT_SORT_KEYT.includes(sortKeyParam as SortKey)
      ? (sortKeyParam as SortKey)
      : null;
  const sortDir: SortDir = searchParams.get("dir") === "desc" ? "desc" : "asc";

  const [sortState, setSortState] = useState<{
    columnKey: string;
    direction: SortDir;
  } | null>(sortKey ? { columnKey: sortKey, direction: sortDir } : null);

  function handleTableSort(columnKey: string, direction: SortDir) {
    setSortState({ columnKey, direction });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("sort", columnKey);
        next.set("dir", direction);
        return next;
      },
      { replace: true },
    );
  }

  // ✅ Add sort value getter
  function getSortValue(k: Kayttaja, key: SortKey): string | number {
    switch (key) {
      case "kayttaja":
        return (naytaNimi(k) ?? k.sahkoposti).toLowerCase();
      case "uid":
        return k.customUid.toLowerCase();
      case "henkilollisyys":
        return k.henkilollisyysTodennettu ? 1 : 0; // Verified first
      case "tila":
        return k.lukittu || k.kayttajatunnusLukittu ? 1 : 0; // Active first
    }
  }

  async function handleHae(e: React.FormEvent) {
    e.preventDefault();

    const normalized = normalisoi(hakuteksti);

    if (normalized.length >= MIN_QUERY_LENGTH && hakuteksti.trim() !== "") {
      setHakuParam(hakuteksti);
      setAdvancedSearchActive(false);
    } else if (Object.values(lisakentat).some((v) => v?.trim())) {
      const filters = Object.fromEntries(
        Object.entries(lisakentat)
          .filter(([, v]) => v?.trim())
          .map(([key, value]) => {
            if (
              (key === "syntymaaika" || key === "voimassaolopaiva") &&
              value?.trim()
            ) {
              const dateRegex = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
              const match = value.match(dateRegex);
              if (match) {
                const day = match[1].padStart(2, "0");
                const month = match[2].padStart(2, "0");
                const year = match[3];
                return [key, `${year}-${month}-${day}`];
              }
            }
            if (key === "myontajamaa" && value?.trim()) {
              const alpha3 = value.trim();
              const alpha2 = alpha3ToAlpha2(alpha3);
              const countryNameFi = alpha2
                ? countries.getName(alpha2, "fi")
                : "";
              const countryNameEn = alpha2
                ? countries.getName(alpha2, "en")
                : "";

              return [
                key,
                {
                  alpha3code: alpha3,
                  alpha2code: alpha2,
                  nameFi: countryNameFi,
                  nameEn: countryNameEn,
                },
              ];
            }
            if (key === "kansalaisuus" && value?.trim()) {
              return [key, alpha3ToAlpha2(value)];
            }
            return [key, value];
          }),
      );

      try {
        const result = await advancedSearch(filters).unwrap();
        setAdvancedSearchResults(result);
        setAdvancedSearchActive(true);
        setHakuParam("");
      } catch (error) {
        console.error("Advanced search failed:", error);
      }
    }
  }

  function handleTyhjenna() {
    setHakuteksti("");
    setLisakentat({});
    setHakuParam("");
    setAdvancedSearchResults([]);
    setAdvancedSearchActive(false);
  }

  const tulokset = advancedSearchActive
    ? advancedSearchResults
    : haku.length >= MIN_QUERY_LENGTH
    ? kayttajat
        .filter((k) => {
          if (tasmaHaku) {
            return (
              k.uid.toLowerCase() === haku ||
              k.customUid.toLowerCase() === haku ||
              k.sahkoposti.toLowerCase() === haku
            );
          }
          const nimi = `${k.etunimi} ${k.sukunimi}`.toLowerCase();
          return (
            k.uid.toLowerCase().includes(haku) ||
            k.customUid.toLowerCase().includes(haku) ||
            k.sahkoposti.toLowerCase().includes(haku) ||
            nimi.includes(haku) ||
            k.etunimi.toLowerCase().includes(haku) ||
            k.sukunimi.toLowerCase().includes(haku)
          );
        })
        .sort((a, b) =>
          `${a.sukunimi} ${a.etunimi}`.localeCompare(
            `${b.sukunimi} ${b.etunimi}`,
            "fi",
          ),
        )
    : [];

  // ✅ Sort results
  const jarjestetty = [...tulokset].sort((a, b) => {
    if (sortKey) {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    }
    return 0;
  });

  function avaaKayttaja(uid: string) {
    navigate(`/kayttajat/${uid}`, {
      state: {
        fromKayttajahaku: true,
        kayttajahakuSearch: location.search,
        kayttajahakuScrollY: window.scrollY,
      },
    });
  }

  function userLink(uid: string) {
    return `/kayttajat/${encodeURIComponent(uid)}`;
  }

  function renderUserLink(uid: string, content: React.ReactNode) {
    return (
      <a
        data-user-link
        data-user-uid={uid}
        href={userLink(uid)}
        onClick={(event) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return;
          event.preventDefault();
          avaaKayttaja(uid);
        }}
      >
        {content}
      </a>
    );
  }

  function tableSortedAriaLiveText(columnLabel: string, direction: SortDir) {
    return `Taulukko lajiteltu ${columnLabel} ${
      direction === "asc" ? "nousevassa" : "laskevassa"
    } järjestyksessä`;
  }

  const naytaTulokset = haku.length >= MIN_QUERY_LENGTH || advancedSearchActive;

  useEffect(() => {
    const table = taulukkoRef.current;
    if (!table) return;

    const buttons =
      table.querySelectorAll<HTMLButtonElement>("thead th button");

    buttons.forEach((button, index) => {
      const column = columns[index];
      if (!column) return;

      const columnLabel = t(column.headerName);

      const sortingDescription =
        sortKey === column.key
          ? t("aria.nykyinenJarjestys", {
              suunta: sortDir === "asc" ? t("aria.nouseva") : t("aria.laskeva"),
            })
          : "";

      button.setAttribute(
        "aria-label",
        t("aria.jarjestaLabel", {
          sarake: columnLabel,
          jarjestys: sortingDescription,
        }),
      );
    });
  }, [t, i18n.language, sortKey, sortDir, naytaTulokset, jarjestetty.length]);

  if (isLoading || kayttajatLataa) {
    return <>{t("common:ladataan")}</>;
  }
  if (!voiNahda("navi.kayttajahaku", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }
  const upota = import.meta.env.VITE_UPOTA_KAYTTAJAHAKU === "true";
  if (upota) {
    return (
      <Iframe
        url="https://dfau.digia.com/ui/accounts"
        width="100%"
        height="950px"
        id="trivoreId"
        className="trivoreIframe"
        display="initial"
        position="relative"
      />
    );
  }
  if (!upota) {
    return (
      <Page>
        <TopRow>
          <Heading variant="h1">
            {t(`sivut.kayttajahaku`, { ns: "common" })}{" "}
          </Heading>
          {naytaTulokset && (
            <Counter>{t("tulos", { count: jarjestetty.length })}</Counter>
          )}
        </TopRow>

        <SearchWrap>
          <form onSubmit={handleHae}>
            <PerusHaku>
              <TextInput
                labelText={t("label")}
                hintText={tasmaHaku ? t("tasmaHakuTeksti") : t("hakuTeksti")}
                visualPlaceholder={
                  tasmaHaku ? t("placeHolderTasma") : t("placeHolder")
                }
                value={hakuteksti}
                onChange={(v) => handleHakuChange((v ?? "").toString())}
                fullWidth
              />
            </PerusHaku>

            {!tasmaHaku && (
              <TarkennettuHaku kentat={lisakentat} onChange={setLisakentta} />
            )}

            <HakuToiminnot>
              <Button type="submit" icon={<IconSearch />}>
                {t("nappi.hae")}
              </Button>
              <Button
                variant="secondary"
                type="button"
                icon={<IconClose />}
                onClick={handleTyhjenna}
              >
                {t("nappi.tyhjenna")}
              </Button>
            </HakuToiminnot>
          </form>
        </SearchWrap>

        {haku.length === 0 && !advancedSearchActive && (
          <StatusMsg>{t("statusMsg")}</StatusMsg>
        )}

        {liianLyhyt && !advancedSearchActive && (
          <StatusMsg>{t("liianLyhyt", { min: MIN_QUERY_LENGTH })}</StatusMsg>
        )}

        {naytaTulokset && jarjestetty.length === 0 && (
          <StatusMsg>
            {advancedSearchActive
              ? t("eiTuloksia.advanced")
              : tasmaHaku
              ? t("eiTuloksia.tasma", { haku: hakuteksti })
              : t("eiTuloksia.vapaa", { haku: hakuteksti })}
          </StatusMsg>
        )}
        {naytaTulokset && jarjestetty.length > 0 && (
          <TableWrapper
            onClick={(event) => {
              const target = event.target;
              if (!(target instanceof Element)) return;
              const row = target.closest("tbody tr");
              if (!row) return;
              const cell = target.closest("td");
              if (cell?.cellIndex === 1) return;
              if (target.closest("button, a, [role=button]")) return;
              const link =
                row.querySelector<HTMLAnchorElement>("a[data-user-link]");
              if (link?.dataset.userUid) avaaKayttaja(link.dataset.userUid);
            }}
          >
            <SuomifiTable
              ref={taulukkoRef}
              columns={columns.map((col) => ({
                key: col.key,
                labelText: t(col.headerName),
                headerName: t(col.headerName),
                width: col.width,
                sortable: true,
                sortIcon: col.sortIcon,
              }))}
              data={jarjestetty.map((k) => {
                const tunnistautumaton = onTunnistautumaton(k);
                const nimiTekstinä = !tunnistautumaton ? naytaNimi(k) : null;
                const primaryTeksti = nimiTekstinä ?? k.sahkoposti;
                const secondaryTeksti = nimiTekstinä ? k.sahkoposti : null;

                return {
                  id: k.uid,
                  kayttaja: renderUserLink(
                    k.uid,
                    <>
                      <KayttajaPrimary>{primaryTeksti}</KayttajaPrimary>
                      {secondaryTeksti && (
                        <KayttajaSecondary>{secondaryTeksti}</KayttajaSecondary>
                      )}
                    </>,
                  ),
                  uid: tunnistautumaton ? (
                    <EiUid>—</EiUid>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <UidWrap>
                        <UidCopy
                          uid={k.customUid}
                          todennettu={k.henkilollisyysTodennettu}
                        />
                      </UidWrap>
                      <VahvistusAlateksti
                        $todennettu={k.henkilollisyysTodennettu}
                      >
                        {t(vahvistusStatus(k), { ns: "common" })}
                      </VahvistusAlateksti>
                    </div>
                  ),
                  henkilollisyys: renderUserLink(
                    k.uid,
                    <HenkilollisyysBadge
                      todennettu={k.henkilollisyysTodennettu}
                    />,
                  ),
                  tila: renderUserLink(
                    k.uid,
                    <TilaTagit>
                      {k.lukittu && (
                        <TilaTag $variant="alert">
                          {t("common:tilatagit.idLukittu")}
                        </TilaTag>
                      )}
                      {k.kayttajatunnusLukittu && (
                        <TilaTag $variant="alert">
                          {t("common:tilatagit.tunnusLukittu")}
                        </TilaTag>
                      )}
                      {k.duplikaattiUid && (
                        <TilaTag $variant="warning">
                          {t("common:tilatagit.duplikaatti")}
                        </TilaTag>
                      )}
                      {!k.lukittu &&
                        !k.kayttajatunnusLukittu &&
                        !k.duplikaattiUid && (
                          <TilaTag $variant="muted">
                            {t("common:tilatagit.aktiivinen")}
                          </TilaTag>
                        )}
                    </TilaTagit>,
                  ),
                };
              })}
              caption={t("sivut.kayttajahaku", { ns: "common" })}
              defaultSort={sortState ?? undefined}
              tableSortCallback={handleTableSort}
              tableSortedAriaLiveText={tableSortedAriaLiveText}
            />
          </TableWrapper>
        )}
      </Page>
    );
  }
}
